import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ProductBase, ProductListItem } from "../../../types/product.type";
import { getListProduct, updateProduct } from "../../../services";
import CataloguePagination from "../shared/Pagination";
import {
  catalogueRowHeight,
  cleanParam,
  cleanStatusParam,
  productDetailsPath,
} from "../shared/constants";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { Theme } from "@mui/material/styles";
import Skeleton from "../../ReprintAuth/Skeleton";
import ProductTableRow from "./Row";
import { WEB_IT_EXPORT_LABEL, toAllowExportParam } from "./webItExport";

/** What a WEB_IT export switch changes on a listed row once the API has saved it. */
type SavedExportFields = Pick<
  ProductBase,
  "allow_export" | "updated_at" | "updated_by_name"
>;

interface ProductTableProps {
  search: string;
  brand: string;
  categoryCode: string;
  status: string;
  allowExport: string;
  triggerFetch: number;
  canEditCatalogue: boolean;
  categoryNameByCode: Map<string, string>;
  onEdit: (productUuid: string) => void;
  onDuplicate: (productUuid: string) => void;
  onDelete: (product: ProductListItem) => void;
  /** A row's WEB_IT export flag was saved, so the count of exported products moved. */
  onExportChanged: () => void;
}

const COLUMNS = [
  "ลำดับสินค้า",
  "รูป",
  "รหัสสินค้า",
  "สินค้า",
  "แบรนด์",
  "หมวดสินค้า",
  "หน่วย",
  "ราคาหน้าซอง",
  "บาร์โค้ดชิ้น",
  "บาร์โค้ดลัง",
  "ขนาด (ซม.)",
  "เลขที่ อย.",
  "สถานะ",
  WEB_IT_EXPORT_LABEL,
  "อัปเดตล่าสุด",
  "การดำเนินการ",
];
const LIMIT = 10;
const THUMBNAIL_SIZE = 36;

/** The product thumbnail is the tallest thing in a row, so it sets the row height. */
const ROW_HEIGHT = catalogueRowHeight(THUMBNAIL_SIZE);

const headCellSx = {
  borderRight: (theme: Theme) => `1px solid ${theme.palette.draft.main}`,
  "&:last-of-type": { borderRight: "none" },
};

export default function ProductTable({
  search,
  brand,
  categoryCode,
  status,
  allowExport,
  triggerFetch,
  canEditCatalogue,
  categoryNameByCode,
  onEdit,
  onDuplicate,
  onDelete,
  onExportChanged,
}: ProductTableProps) {
  const navigate = useNavigate();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(LIMIT);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [products, setProducts] = useState<ProductListItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [savingExportUuid, setSavingExportUuid] = useState<string | null>(null);

  // A filter change has to restart the list from the first page. Doing it here
  // rather than in an effect keeps it in the same render as the change, so the
  // fetch below runs once with the new page instead of once per page value.
  const filterSignature = JSON.stringify({
    search,
    brand,
    categoryCode,
    status,
    allowExport,
  });
  const [appliedFilterSignature, setAppliedFilterSignature] =
    useState<string>(filterSignature);

  if (filterSignature !== appliedFilterSignature) {
    setAppliedFilterSignature(filterSignature);
    setPage(1);
  }

  useEffect(() => {
    // A slower earlier response must not overwrite the rows of a later request.
    let ignored = false;

    const getProducts = async () => {
      setLoading(true);
      try {
        const {
          data: { data },
        } = await getListProduct(page, limit, {
          search: search || undefined,
          brand: brand || undefined,
          category_code: cleanParam(categoryCode),
          status: cleanStatusParam(status),
          allow_export: toAllowExportParam(allowExport),
        });

        if (ignored) return;
        setProducts(data.rows);
        setTotalCount(data.count);
      } finally {
        if (!ignored) setLoading(false);
      }
    };

    getProducts();

    return () => {
      ignored = true;
    };
  }, [
    triggerFetch,
    page,
    limit,
    search,
    brand,
    categoryCode,
    status,
    allowExport,
  ]);

  const handleView = (productUuid: string) => {
    navigate(productDetailsPath(productUuid));
  };

  const applySavedExport = (productUuid: string, saved: SavedExportFields) => {
    setProducts((prev) =>
      prev.map((row) =>
        row.uuid === productUuid
          ? {
              ...row,
              allow_export: saved.allow_export,
              updated_at: saved.updated_at,
              updated_by_name: saved.updated_by_name,
            }
          : row,
      ),
    );
  };

  // The row is patched in place rather than the page re-read, so the list does not
  // flash its loading skeleton for a one-field change.
  const handleToggleExport = async (
    product: ProductListItem,
    nextAllowExport: boolean,
  ) => {
    setSavingExportUuid(product.uuid);
    try {
      const {
        data: { data },
      } = await updateProduct(product.uuid, { allow_export: nextAllowExport });
      applySavedExport(product.uuid, data);
      onExportChanged();
    } catch {
      // The axios interceptor reports the failure; the switch keeps the stored value.
    } finally {
      setSavingExportUuid(null);
    }
  };

  const resolveCategoryName = (categoryCode: string | null): string =>
    categoryCode
      ? categoryNameByCode.get(categoryCode) ?? categoryCode
      : "";

  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead
            sx={{ bgcolor: (theme) => theme.palette.background.default }}
          >
            <TableRow>
              {COLUMNS.map((column) => (
                <TableCell key={column} sx={headCellSx}>
                  {column}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <Skeleton
                columns={COLUMNS.length}
                rows={limit}
                rowHeight={ROW_HEIGHT}
              />
            )}
            {!loading && products.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={COLUMNS.length}
                  align="center"
                  sx={{ py: 5 }}
                >
                  <Typography color="text.secondary" fontWeight={500}>
                    ไม่พบข้อมูลสินค้า
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              products.map((product, index) => (
                <ProductTableRow
                  key={product.uuid}
                  product={product}
                  rowHeight={ROW_HEIGHT}
                  thumbnailSize={THUMBNAIL_SIZE}
                  striped={index % 2 === 1}
                  categoryName={resolveCategoryName(product.category_code)}
                  canEdit={canEditCatalogue}
                  isExportSaving={savingExportUuid === product.uuid}
                  onToggleExport={handleToggleExport}
                  onView={handleView}
                  onEdit={onEdit}
                  onDuplicate={onDuplicate}
                  onDelete={onDelete}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <CataloguePagination
        totalCount={totalCount}
        page={page}
        setPage={setPage}
        limit={limit}
        setLimit={setLimit}
      />
    </Paper>
  );
}
